/**
 * Iter 67bg — Fleet health dashboard tile.
 *
 * Top-of-vendor-dashboard one-glance summary of the 3 best and 3 worst
 * performing cars by YTD Net. Clicking a card jumps into Fleet finance
 * (or directly to the public car detail if the user wants to share/edit
 * pricing).
 *
 * Silently hides itself when the fleet has zero activity (no bookings,
 * no expenses) — same restraint pattern as the TCO chip, so the dashboard
 * isn't cluttered for vendors just getting started.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Money from "@/components/Money";
import { TrendingUp, TrendingDown, Activity, ArrowRight, AlertTriangle, BellOff } from "lucide-react";
import { toast } from "sonner";

function _YoyChip({ current, previous, testId }) {
  // Iter 67bn — compact "▲ +€340 vs 2025" / "▼ −€120" delta chip.
  // Hides itself when both years are zero (no signal to show).
  const delta = Number((current || 0) - (previous || 0));
  if (current === 0 && previous === 0) return null;
  const up = delta >= 0;
  const tone = up
    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
    : "bg-rose-50 text-rose-700 ring-rose-200";
  const sign = up ? "▲" : "▼";
  const abs = Math.abs(delta);
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ring-1 ${tone} text-[10px] font-bold tabular-nums`} data-testid={testId}>
      {sign} <Money amount={abs} />
    </span>
  );
}

function _CarHealthRow({ row, tone, rank, testId, showYoy, onUnsnooze }) {
  const navigate = useNavigate();
  const ringTone = tone === "best"
    ? "ring-emerald-100 hover:ring-emerald-200 hover:bg-emerald-50/40"
    : "ring-rose-100 hover:ring-rose-200 hover:bg-rose-50/40";
  const rankBgTone = tone === "best" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700";
  const netTone = tone === "best" ? "text-emerald-700" : "text-rose-700";

  // Vendor's most useful action on a worst-performer is to review pricing /
  // expenses → drop them into Fleet finance with the right car preselected.
  const onClick = () => {
    navigate(`/dashboard/vendor?tab=fleet-finance&car=${row.id}`);
  };

  // Iter 67cd — compute days left if the row is snoozed so we render
  // "Snoozed (N days left)" pill on the row. Stops the vendor wondering
  // whether they actually fixed the issue or just hid it.
  const snoozeDaysLeft = (() => {
    if (!row.alert_snoozed_until) return null;
    const ms = new Date(row.alert_snoozed_until).getTime() - Date.now();
    if (!Number.isFinite(ms) || ms <= 0) return null;
    return Math.max(1, Math.ceil(ms / 86_400_000));
  })();

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group w-full text-left flex items-center gap-3 p-3 rounded-xl bg-white ring-1 ${ringTone} transition-all`}
      data-testid={testId}
    >
      <div className={`flex-shrink-0 h-7 w-7 rounded-lg flex items-center justify-center font-bold text-xs ${rankBgTone}`}>
        #{rank}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-neutral-900 text-sm truncate">{row.label}</div>
        <div className="text-[11px] text-neutral-500 truncate">
          {row.plate ? `${row.plate} · ` : ""}
          Rev <Money amount={row.revenue_eur} /> · Exp <Money amount={row.expenses_eur} />
        </div>
      </div>
      <div className="text-right flex-shrink-0 flex items-center gap-1.5">
        {row.alert_reasons && row.alert_reasons.length > 0 && (
          <span
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ring-1 bg-amber-50 text-amber-800 ring-amber-200 text-[10px] font-bold"
            title={row.alert_reasons.join(" · ")}
            data-testid={`${testId}-alert`}
          >
            <AlertTriangle className="h-3 w-3" /> Alert
          </span>
        )}
        {snoozeDaysLeft != null && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); onUnsnooze?.(row.id); }}
            onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); e.preventDefault(); onUnsnooze?.(row.id); } }}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md ring-1 bg-neutral-100 text-neutral-700 ring-neutral-200 text-[10px] font-bold hover:bg-rose-50 hover:text-rose-700 hover:ring-rose-200 transition cursor-pointer"
            title={`Alerts muted for ${snoozeDaysLeft} more day${snoozeDaysLeft === 1 ? "" : "s"}. Click to unmute.`}
            data-testid={`${testId}-snoozed`}
          >
            <BellOff className="h-3 w-3" /> Snoozed · {snoozeDaysLeft}d left
          </span>
        )}
        {showYoy && row.prev_net_eur != null && (
          <_YoyChip current={row.net_eur} previous={row.prev_net_eur} testId={`${testId}-yoy`} />
        )}
        <div>
          <div className={`font-bold tabular-nums text-sm ${netTone}`}><Money amount={row.net_eur} /></div>
          <div className="text-[9px] uppercase tracking-wider text-neutral-500">Net</div>
        </div>
      </div>
      <ArrowRight className={`h-3.5 w-3.5 text-neutral-400 group-hover:translate-x-0.5 transition-transform ${tone === "worst" ? "group-hover:text-rose-600" : "group-hover:text-emerald-600"}`} />
    </button>
  );
}

export default function FleetHealthTile() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  // Iter 67bn — YoY toggle. Persisted via the backend `compare_yoy` flag so
  // the prev-year aggregates only come down when actually needed.
  const [showYoy, setShowYoy] = useState(false);
  const [snoozing, setSnoozing] = useState(null);

  const reload = (yoy = showYoy) => api.get("/vendor/fleet-health", { params: { compare_yoy: yoy } })
    .then((r) => setData(r.data))
    .catch(() => setError(true));

  useEffect(() => {
    let alive = true;
    api.get("/vendor/fleet-health", { params: { compare_yoy: showYoy } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, [showYoy]);

  const onSnooze = async (e, carId, days = 7) => {
    e.preventDefault();
    e.stopPropagation();
    setSnoozing(carId);
    try {
      await api.post(`/vendor/threshold-breach-snooze?car_id=${encodeURIComponent(carId)}&days=${days}`);
      toast.success(`Muted for ${days} days. You won't get more breach emails for this car until then.`);
      await reload();
    } catch (err) {
      toast.error(formatApiError(err) || "Snooze failed.");
    } finally {
      setSnoozing(null);
    }
  };

  const onUnsnooze = async (carId) => {
    setSnoozing(carId);
    try {
      await api.delete(`/vendor/threshold-breach-snooze/${encodeURIComponent(carId)}`);
      toast.success("Alerts un-muted.");
      await reload();
    } catch (err) {
      toast.error(formatApiError(err) || "Unsnooze failed.");
    } finally {
      setSnoozing(null);
    }
  };

  if (error) return null;
  if (!data) return null;
  // Wait for the dashboard to have some activity before showing the tile
  // — empty fleet health on Day 1 is noise.
  if ((data.best || []).length === 0 && (data.worst || []).length === 0) {
    return null;
  }

  const hasWorst = (data.worst || []).length > 0;
  // Iter 67cc — collect every car with a threshold breach so we can render a
  // compact pill cluster at the top of the tile. Mirrors the admin Fleet Health
  // chip's "threshold breaches" row but scoped to this vendor's own fleet.
  const breachingCars = [...(data.best || []), ...(data.worst || [])]
    .filter((c) => Array.isArray(c.alert_reasons) && c.alert_reasons.length > 0)
    .filter((c, idx, arr) => arr.findIndex((x) => x.id === c.id) === idx);

  return (
    <div className="mb-8 rounded-2xl border border-emerald-100 bg-gradient-to-br from-white via-emerald-50/30 to-rose-50/20 p-5 shadow-sm" data-testid="fleet-health-tile">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white shadow-md shadow-teal-200">
            <Activity className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-teal-700">Fleet health · {data.year}</div>
            <div className="font-semibold text-neutral-900 text-sm">Best & worst performers by YTD Net</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Iter 67bn — YoY comparison toggle. Rendered as a pill so it
              matches the dashboard's other small filters. */}
          <button
            type="button"
            onClick={() => setShowYoy((v) => !v)}
            className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full ring-1 transition ${
              showYoy
                ? "bg-teal-600 text-white ring-teal-700 shadow-sm"
                : "bg-white text-teal-700 ring-teal-200 hover:bg-teal-50"
            }`}
            aria-pressed={showYoy}
            data-testid="fleet-health-yoy-toggle"
          >
            <TrendingUp className="h-3 w-3" />
            vs {data.prev_year ?? data.year - 1}
          </button>
          <Link
            to="/dashboard/vendor?tab=fleet-finance"
            className="text-xs font-semibold text-teal-700 hover:text-teal-900 inline-flex items-center gap-1"
            data-testid="fleet-health-open"
          >
            Open Fleet finance <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {breachingCars.length > 0 && (
        <div className="mb-4 rounded-xl bg-amber-50/70 border border-amber-200 p-3" data-testid="fleet-health-alert-cluster">
          <div className="flex items-center gap-2 mb-2 text-[11px] font-bold text-amber-900 uppercase tracking-wider">
            <AlertTriangle className="h-3.5 w-3.5" /> {breachingCars.length} threshold breach{breachingCars.length === 1 ? "" : "es"} this year
          </div>
          <div className="flex flex-wrap gap-2">
            {breachingCars.slice(0, 8).map((c) => (
              <div
                key={c.id}
                className="inline-flex items-center bg-white rounded-full ring-1 ring-amber-200 hover:ring-amber-400 text-[11px] font-medium text-amber-900 transition overflow-hidden"
                data-testid={`fleet-health-alert-pill-${c.id}`}
              >
                <Link
                  to={`/dashboard/vendor?tab=fleet-finance&car=${c.id}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 hover:bg-amber-100/40 transition"
                  title={c.alert_reasons.join(" · ")}
                >
                  <span className="font-semibold">{c.label}</span>
                  {c.plate && <span className="text-amber-700 text-[10px]">· {c.plate}</span>}
                  <span className="bg-amber-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">{c.alert_reasons[0]}</span>
                  <ArrowRight className="h-3 w-3" />
                </Link>
                <button
                  type="button"
                  onClick={(e) => onSnooze(e, c.id, 7)}
                  disabled={snoozing === c.id}
                  className="border-l border-amber-200 px-2 py-1 text-amber-700 hover:bg-amber-100/60 disabled:opacity-40 inline-flex items-center gap-1 transition"
                  title="Mute alerts for this car for 7 days"
                  data-testid={`fleet-health-alert-snooze-${c.id}`}
                >
                  <BellOff className="h-3 w-3" />
                  <span className="text-[10px] font-semibold">7d</span>
                </button>
              </div>
            ))}
            {breachingCars.length > 8 && (
              <span className="text-[11px] text-amber-800 italic self-center">+{breachingCars.length - 8} more</span>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <div className="flex items-center gap-1.5 mb-2.5 text-[11px] font-bold text-emerald-700 uppercase tracking-wider">
            <TrendingUp className="h-3.5 w-3.5" /> Top performers
          </div>
          <div className="space-y-2">
            {(data.best || []).map((row, i) => (
              <_CarHealthRow key={row.id} row={row} rank={i + 1} tone="best" testId={`fleet-health-best-${row.id}`} showYoy={showYoy} onUnsnooze={onUnsnooze} />
            ))}
            {(data.best || []).length === 0 && (
              <div className="text-xs text-neutral-500 italic px-2 py-3">No revenue this year yet.</div>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center gap-1.5 mb-2.5 text-[11px] font-bold text-rose-700 uppercase tracking-wider">
            <TrendingDown className="h-3.5 w-3.5" /> Needs attention
          </div>
          <div className="space-y-2">
            {hasWorst && (data.worst || []).map((row, i) => (
              <_CarHealthRow key={row.id} row={row} rank={i + 1} tone="worst" testId={`fleet-health-worst-${row.id}`} showYoy={showYoy} onUnsnooze={onUnsnooze} />
            ))}
            {!hasWorst && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50/60 border border-amber-100 text-xs text-amber-900">
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                <span>All your vehicles are net-positive YTD — keep it up.</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
